import { useEffect, useState } from "react";
import { AlertCircle, Check, Copy, ExternalLink, Loader2, Package, Plus } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { PackActionResponse, PackListResponse, PackSummary } from "../types";
import { ScreenHeader } from "./ScreenHeader";
import { openPackLink } from "../lib/telegram";

interface SuccessResult {
  created: boolean;
  packTitle: string;
  packLink: string | null;
  stickerCount: number;
  emoji: string;
}

type Busy = { kind: "create" | "add"; title: string } | null;

const STEP_MS = 1500;

function stepsFor(busy: NonNullable<Busy>): string[] {
  return busy.kind === "create"
    ? ["جاري إنشاء الحزمة…", "رفع الملصق إلى تيليجرام…", "تجهيز رابط الحزمة…"]
    : ["جاري رفع الملصق…", `إضافته إلى «${busy.title}»…`, "تحديث الحزمة…"];
}

/** Full-screen progress state shown while the server talks to Telegram (it can take a few seconds). */
function ProgressView({ busy }: { busy: NonNullable<Busy> }) {
  const steps = stepsFor(busy);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setStep((s) => Math.min(s + 1, steps.length - 1)), STEP_MS);
    return () => window.clearInterval(id);
  }, [steps.length]);

  return (
    <div className="flex flex-col h-full" role="status" aria-live="polite">
      <div className="flex-1 flex flex-col items-center justify-center gap-6 p-6">
        <div className="relative w-20 h-20 flex items-center justify-center">
          <span className="absolute inset-0 rounded-full glass-pill" />
          <Loader2 className="relative text-tg-button animate-spin" size={38} strokeWidth={2.25} />
        </div>

        <div className="text-center">
          <p className="font-semibold text-base">{busy.kind === "create" ? "جاري إنشاء الحزمة" : "جاري الإضافة"}</p>
          <p key={step} className="text-tg-hint text-sm mt-1.5 min-h-[1.25rem]">
            {steps[step]}
          </p>
        </div>

        <div className="flex items-center gap-1.5" aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full bg-tg-button transition-all duration-500"
              style={{ width: i === step ? 22 : 6, opacity: i <= step ? 1 : 0.25 }}
            />
          ))}
        </div>

        <p className="text-tg-hint text-xs text-center max-w-[16rem]">لا تغلق الشاشة حتى تكتمل العملية</p>
      </div>
    </div>
  );
}

export function PackPicker({
  stickerId,
  detectedEmoji,
  onBack,
  onDone,
}: {
  stickerId: string;
  detectedEmoji?: string | null;
  onBack: () => void;
  onDone: () => void;
}) {
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(false);
  // Pre-filled with the detected emoji as a convenience, but this is a
  // free-text field - the user can type/paste any emoji themselves, or
  // clear it entirely ("تخطي") to fall back to the default 👍. The
  // server independently re-validates this; a bad value here just
  // means the server falls back to 👍 too, never a hard failure.
  const [emojiInput, setEmojiInput] = useState(detectedEmoji ?? "");

  const EMOJI_ONLY_PATTERN =
    /^(?:[\u{1F1E6}-\u{1F1FF}]{2}|[0-9#*]\u{FE0F}?\u{20E3}|(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}])(?:\u{FE0F})?(?:\u{200D}(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}])(?:\u{FE0F})?)*)$/u;
  const trimmedEmoji = emojiInput.trim();
  const emojiLooksValid = !trimmedEmoji || EMOJI_ONLY_PATTERN.test(trimmedEmoji);
  const chosenEmoji = trimmedEmoji && emojiLooksValid ? trimmedEmoji : undefined;

  useEffect(() => {
    api
      .get<PackListResponse>("/api/packs")
      .then((res) => setPacks(res.packs))
      .catch(() => setPacks([]));
  }, []);

  // Small entrance animation for the success card.
  useEffect(() => {
    if (!success) {
      setShown(false);
      return;
    }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [success]);

  const addToExisting = async (pack: PackSummary) => {
    if (busy) return;
    setBusy({ kind: "add", title: pack.title });
    setError(null);
    try {
      const res = await api.post<PackActionResponse>(`/api/packs/${encodeURIComponent(pack.name)}/stickers`, {
        pack_name: pack.name,
        sticker_id: stickerId,
        emoji: chosenEmoji,
      });
      if (!res.ok) {
        setError(res.error ?? "فشلت الإضافة");
      } else {
        setSuccess({
          created: false,
          packTitle: res.pack_title,
          packLink: res.pack_link ?? null,
          stickerCount: res.sticker_count,
          emoji: chosenEmoji ?? "👍",
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "حدث خطأ");
    } finally {
      setBusy(null);
    }
  };

  const createNew = async () => {
    const title = newTitle.trim();
    if (!title || busy) return;
    setBusy({ kind: "create", title });
    setError(null);
    try {
      const res = await api.post<PackActionResponse>("/api/packs", {
        title,
        sticker_id: stickerId,
        emoji: chosenEmoji,
      });
      if (!res.ok) {
        setError(res.error ?? "فشل إنشاء الحزمة");
      } else {
        setSuccess({
          created: true,
          packTitle: res.pack_title,
          packLink: res.pack_link ?? null,
          stickerCount: res.sticker_count,
          emoji: chosenEmoji ?? "👍",
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "حدث خطأ");
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* nothing else to try */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  if (busy) return <ProgressView busy={busy} />;

  if (success) {
    return (
      <div className="flex flex-col h-full">
        <ScreenHeader title="تمت الإضافة" />
        <div
          className="p-5 flex flex-col items-center gap-5 flex-1 justify-center transition-all duration-500"
          style={{ opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(10px) scale(0.98)" }}
        >
          <div className="w-16 h-16 rounded-full glass-pill flex items-center justify-center">
            <Check className="text-vinyl-mint" size={30} strokeWidth={2.5} />
          </div>

          <div className="text-center">
            <p className="font-semibold text-lg">{success.created ? "تم إنشاء الحزمة بنجاح" : "تمت إضافة الملصق بنجاح"}</p>
            <p className="text-tg-hint text-sm mt-1">
              {success.created ? "أُضيف ملصقك كأول ملصق في الحزمة" : "أصبح الملصق ضمن الحزمة الآن"}
            </p>
          </div>

          <div className="glass-card w-full max-w-xs p-4 flex items-center gap-3">
            <span className="glass-pill w-12 h-12 rounded-xl flex items-center justify-center shrink-0">
              <Package className="text-vinyl-violet" size={22} strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1 text-right">
              <p className="font-semibold truncate">{success.packTitle}</p>
              <p className="text-tg-hint text-xs mt-0.5">
                {success.stickerCount} ملصق · الإيموجي {success.emoji}
              </p>
            </div>
          </div>

          {success.packLink && (
            <div className="w-full max-w-xs flex flex-col gap-3">
              <button
                type="button"
                onClick={() => openPackLink(success.packLink!)}
                className="w-full px-4 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold flex items-center justify-center gap-2"
              >
                <Package size={18} strokeWidth={2.25} />
                فتح الحزمة في تيليجرام
              </button>

              <button
                type="button"
                onClick={() => copyLink(success.packLink!)}
                className="w-full glass-pill px-4 py-3 rounded-xl text-tg-text flex items-center justify-center gap-2 text-sm"
              >
                {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2.25} />}
                {copied ? "تم نسخ الرابط" : "نسخ رابط الحزمة"}
              </button>

              <a
                href={success.packLink}
                onClick={(e) => {
                  e.preventDefault();
                  openPackLink(success.packLink!);
                }}
                className="text-tg-link text-xs break-all text-center flex items-center justify-center gap-1"
                dir="ltr"
              >
                <ExternalLink size={12} />
                {success.packLink}
              </a>
            </div>
          )}

          <button type="button" onClick={onDone} className="text-tg-hint text-sm px-5 py-2">
            تم
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <ScreenHeader title="أضف الملصق إلى حزمة" onBack={onBack} />
      <div className="p-4 flex flex-col gap-6">
        {error && (
          <div className="glass-card p-3 flex items-start gap-2 text-red-400 text-sm" role="alert">
            <AlertCircle size={18} strokeWidth={2.25} className="shrink-0 mt-0.5" />
            <p>{error} — يمكنك إعادة المحاولة.</p>
          </div>
        )}

        <div className="glass-card p-4">
          <label className="block text-sm text-tg-hint mb-2">إيموجي الملصق (اختياري)</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={emojiInput}
              onChange={(e) => setEmojiInput(e.target.value)}
              placeholder="👍 افتراضي"
              dir="ltr"
              maxLength={8}
              className="flex-1 min-w-0 rounded-xl glass-pill px-4 py-3 text-base text-center outline-none placeholder:text-tg-hint"
            />
            {emojiInput && (
              <button
                type="button"
                onClick={() => setEmojiInput("")}
                className="shrink-0 glass-pill px-4 py-3 rounded-xl text-sm text-tg-hint"
              >
                تخطي
              </button>
            )}
          </div>
          {trimmedEmoji && !emojiLooksValid && (
            <p className="text-red-400 text-xs mt-2">الرجاء إدخال إيموجي واحد صحيح، أو اضغط "تخطي"</p>
          )}
        </div>

        <div className="glass-card p-4">
          <label className="block text-sm text-tg-hint mb-2">حزمة جديدة</label>
          {/* Stacked, full-width - and text-base (16px) is required on
              this input: anything smaller triggers iOS Safari/WebView's
              automatic zoom-on-focus, which visibly shifts the whole
              screen and can push a same-row button off the visible
              area. Same lesson as the editor's text input. */}
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="اسم الحزمة"
            dir="rtl"
            className="w-full rounded-xl glass-pill px-4 py-3 text-base outline-none placeholder:text-tg-hint mb-3"
          />
          <button
            type="button"
            disabled={!newTitle.trim()}
            onClick={createNew}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold disabled:opacity-40"
          >
            <Plus size={18} strokeWidth={2.5} />
            إنشاء حزمة جديدة
          </button>
        </div>

        <div>
          <label className="block text-sm text-tg-hint mb-2">أو أضف لحزمة موجودة</label>
          {packs === null && <p className="text-tg-hint text-sm">جاري التحميل...</p>}
          {packs && packs.length === 0 && <p className="text-tg-hint text-sm">لا توجد حزم بعد</p>}

          {/* Horizontal swipeable row instead of a long vertical list -
              stays usable even with many packs. touchAction: "pan-x"
              (not the no-native-drag/touch-action:none class used for
              drag gestures elsewhere) is required here so touch-swipe
              actually scrolls it - a plain overflow-x-auto container
              with the wrong touch-action silently becomes unswipeable
              on mobile, exactly what broke the effects tab bar earlier. */}
          {packs && packs.length > 0 && (
            <div
              className="flex gap-3 overflow-x-auto -mx-1 px-1 py-1 snap-x snap-mandatory"
              style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch", touchAction: "pan-x" }}
            >
              {packs.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => addToExisting(p)}
                  className="shrink-0 snap-start w-36 glass-card p-4 flex flex-col items-start gap-2 text-right"
                >
                  <span className="glass-pill w-10 h-10 rounded-xl flex items-center justify-center">
                    <Package className="text-vinyl-violet" size={18} strokeWidth={2.25} />
                  </span>
                  <span className="font-semibold text-sm truncate w-full">{p.title}</span>
                  <span className="text-tg-hint text-xs">{p.sticker_count} ملصق</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
